import type { AppDispatch, AppThunk } from "~/app/store";
import { TranslationType } from "~/common/types/TranslationModel";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import {
  executeAddToCart,
  type AddToCartOptions,
  type AddToCartSuccessContext,
} from "~/features/addToCart/redux/addToCart/addToCartCommand";
import type { AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import { clearCart } from "~/features/cart/cart/redux/actions/cartPageActions";
import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useTranslation } from "react-i18next";
import { Snackbar } from "~/common/mui";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";

export {
  mapAddToCartItemToCartApiItem,
  mapAddToCartItemToQuoteApiItem,
} from "~/features/addToCart/redux/addToCart/addToCartCommand";
export type {
  AddToCartErrorUi,
  AddToCartItem,
  AddToCartOptions,
  AddToCartPartItem,
  AddToCartSuccessUi,
} from "~/features/addToCart/redux/addToCart/addToCartCommand";

export function addToCart(options: AddToCartOptions): AppThunk<Promise<AddToCartResponseItem[]>> {
  return (dispatch) =>
    dispatch(
      executeAddToCart(options, {
        clearCart: () => dispatch(clearCart()),
        onSuccess: (context) => {
          showSuccessUi(context, dispatch);
        },
      }),
    );
}

function showSuccessUi(
  { successUi, page, responseItems, initialCount, isSalesman }: AddToCartSuccessContext,
  dispatch: AppDispatch,
) {
  if (successUi === "modal") {
    const resultCount = totalQuantity(responseItems);
    const quantityAdjusted = resultCount !== initialCount;

    // No better place to show it right now..
    if (quantityAdjusted && isPhaseOut2Enabled() && page === "WISHLIST") {
      openModal("qty-adjusted-snackbar", (close) => <QtyAdjustedSnackbar close={close} />);
    }

    openModal("add-to-cart-dialog", (close) => (
      <CartSuccessModal onClose={close} page={page} responseItems={responseItems} />
    ));
  }

  if (successUi === "toast") {
    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: isSalesman ? "addToQuote.success" : "addToCart.success",
        params: { count: initialCount },
      }),
    );
  }
}

function totalQuantity(items: { quantity: number }[]) {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

const QtyAdjustedSnackbar = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();

  return (
    <Snackbar
      open={true}
      message={t("product.phaseout.qtyAdjusted.past", "Your quantity has been adjusted")}
      onClose={close}
      action={<SnackbarCloseButton onClick={close} />}
    />
  );
};
